import { useState, useEffect } from 'react';
import type { PriceData, PriceBar, PriceCoin, PriceScrapGrade, PriceSource } from '../types/prices';
import '../styles/globals.css';
import './Admin.css';

type Section = 'bars' | 'coins' | 'scrap';
type Message = { type: 'ok' | 'error'; text: string } | null;

type Overrides = {
  bars: Record<string, number>;
  coins: Record<string, number>;
  scrap: Record<string, { particulier?: number; bedrijf?: number }>;
  custom: { bars: PriceBar[]; coins: PriceCoin[]; scrap: PriceScrapGrade[] };
};

const EMPTY: Overrides = { bars: {}, coins: {}, scrap: {}, custom: { bars: [], coins: [], scrap: [] } };
const TAB_LABEL: Record<Section, string> = { bars: 'Goudstaven', coins: 'Goudmunten', scrap: 'Sloopgoud' };

export function Admin() {
  const [baseline, setBaseline] = useState<PriceData | null>(null);
  const [freshness, setFreshness] = useState<{ fetchedAt?: string; priceSource?: PriceSource }>({});
  const [ov, setOv] = useState<Overrides>(EMPTY);
  const [password, setPassword] = useState('');
  const [tab, setTab] = useState<Section>('bars');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<Message>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [bRes, oRes] = await Promise.all([
        fetch('/api/argentor?baseline=1'),
        fetch('/api/argentor?overrides=1'),
      ]);
      const b = await bRes.json();
      const o = await oRes.json();
      if (b.success) {
        setBaseline({ bars: b.bars, coins: b.coins, scrap: b.scrap });
        setFreshness({ fetchedAt: b.fetchedAt, priceSource: b.priceSource });
      }
      if (o.success) setOv({
        bars: o.bars ?? {}, coins: o.coins ?? {}, scrap: o.scrap ?? {},
        custom: o.custom ?? { bars: [], coins: [], scrap: [] },
      });
      if (!b.success) setMsg({ type: 'error', text: 'Kon marktprijzen niet laden.' });
    } catch { setMsg({ type: 'error', text: 'Kon gegevens niet laden.' }); }
    finally { setLoading(false); }
  }

  async function save() {
    if (!password) { setMsg({ type: 'error', text: 'Vul eerst het wachtwoord in.' }); return; }
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch('/api/argentor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
        body: JSON.stringify({ bars: ov.bars, coins: ov.coins, scrap: ov.scrap, custom: ov.custom }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) setMsg({ type: 'ok', text: 'Opgeslagen. Vastgezette prijzen blijven staan, de rest blijft automatisch updaten.' });
      else if (res.status === 401) setMsg({ type: 'error', text: 'Ongeldig wachtwoord.' });
      else setMsg({ type: 'error', text: json.error || 'Opslaan mislukt.' });
    } catch { setMsg({ type: 'error', text: 'Opslaan mislukt.' }); }
    finally { setSaving(false); }
  }

  // --- pin / unpin helpers ---
  const pin = (section: 'bars' | 'coins', name: string, value: number) =>
    setOv(o => ({ ...o, [section]: { ...o[section], [name]: value } }));
  const unpin = (section: 'bars' | 'coins', name: string) =>
    setOv(o => { const m = { ...o[section] }; delete m[name]; return { ...o, [section]: m }; });
  const pinScrap = (name: string, field: 'particulier' | 'bedrijf', value: number) =>
    setOv(o => ({ ...o, scrap: { ...o.scrap, [name]: { ...o.scrap[name], [field]: value } } }));
  const unpinScrap = (name: string) =>
    setOv(o => { const m = { ...o.scrap }; delete m[name]; return { ...o, scrap: m }; });

  // --- custom items ---
  const addCustom = (section: Section) =>
    setOv(o => {
      const item = section === 'scrap' ? { name: '', particulier: 0, bedrijf: 0 } : { name: '', weight: '', price: 0 };
      return { ...o, custom: { ...o.custom, [section]: [...o.custom[section], item] } };
    });
  const setCustom = (section: Section, i: number, field: string, value: string | number) =>
    setOv(o => ({ ...o, custom: { ...o.custom, [section]: (o.custom[section] as unknown as Record<string, unknown>[]).map((r, idx) => idx === i ? { ...r, [field]: value } : r) } }));
  const removeCustom = (section: Section, i: number) =>
    setOv(o => ({ ...o, custom: { ...o.custom, [section]: (o.custom[section] as unknown[]).filter((_, idx) => idx !== i) } }));

  const num = (v: string) => parseFloat(v) || 0;

  return (
    <div className="admin">
      <div className="admin__container">
        <header className="admin__head">
          <h1 className="admin__title">Prijzenbeheer — Ruxia Gold</h1>
          <p className="admin__sub">
            Alle prijzen updaten automatisch op basis van de markt.
            Pas je een prijs aan, dan blijft <strong>enkel die prijs</strong> vast staan tot je hem terugzet op automatisch.
          </p>
          {freshness.fetchedAt && (
            <p className={`admin__freshness admin__freshness--${freshness.priceSource === 'live' ? 'ok' : 'warn'}`}>
              Marktprijzen opgehaald op {new Date(freshness.fetchedAt).toLocaleString('nl-BE')} —{' '}
              {freshness.priceSource === 'live'
                ? 'live koers'
                : 'staven en munten live, sloopgoud uit een verouderde cache — controleer de scraping-credits'}
            </p>
          )}
        </header>

        {msg && <div className={`admin__msg admin__msg--${msg.type}`}>{msg.text}</div>}

        <div className="admin__toolbar">
          <input
            type="password" className="admin__pw" placeholder="Wachtwoord"
            value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
          />
          <button className="btn btn--primary" onClick={save} disabled={saving || loading}>
            {saving ? 'Opslaan…' : 'Opslaan'}
          </button>
          <button className="btn btn--ghost" onClick={load} disabled={loading || saving}>Herladen</button>
        </div>

        <div className="admin__tabs">
          {(['bars', 'coins', 'scrap'] as Section[]).map(t => (
            <button key={t} className={`admin__tab ${tab === t ? 'admin__tab--active' : ''}`} onClick={() => setTab(t)}>
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>

        {loading || !baseline ? (
          <div className="admin__loading">Laden…</div>
        ) : tab === 'scrap' ? (
          <table className="admin__table">
            <thead>
              <tr><th>Kwaliteit</th><th>Particulier €/g</th><th>Bedrijf €/g</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {baseline.scrap.map(row => {
                const o = ov.scrap[row.name];
                const pinned = !!o && (o.particulier !== undefined || o.bedrijf !== undefined);
                return (
                  <tr key={row.name} className={pinned ? 'admin__row--pinned' : ''}>
                    <td className="admin__name">{row.name}</td>
                    <td><input className="admin__in admin__in--num" type="number" step="0.01"
                      value={o?.particulier ?? row.particulier}
                      onChange={e => pinScrap(row.name, 'particulier', num(e.target.value))} /></td>
                    <td><input className="admin__in admin__in--num" type="number" step="0.01"
                      value={o?.bedrijf ?? row.bedrijf}
                      onChange={e => pinScrap(row.name, 'bedrijf', num(e.target.value))} /></td>
                    <td>{pinned
                      ? <span className="admin__badge admin__badge--pinned">Handmatig</span>
                      : <span className="admin__badge">Auto</span>}</td>
                    <td>{pinned && <button className="admin__revert" title="Terug naar automatisch" onClick={() => unpinScrap(row.name)}>↺ auto</button>}</td>
                  </tr>
                );
              })}
              {ov.custom.scrap.map((row, i) => (
                <tr key={`c${i}`} className="admin__row--pinned">
                  <td><input className="admin__in" placeholder="Naam" value={row.name} onChange={e => setCustom('scrap', i, 'name', e.target.value)} /></td>
                  <td><input className="admin__in admin__in--num" type="number" step="0.01" value={row.particulier} onChange={e => setCustom('scrap', i, 'particulier', num(e.target.value))} /></td>
                  <td><input className="admin__in admin__in--num" type="number" step="0.01" value={row.bedrijf} onChange={e => setCustom('scrap', i, 'bedrijf', num(e.target.value))} /></td>
                  <td><span className="admin__badge admin__badge--pinned">Eigen</span></td>
                  <td><button className="admin__del" title="Verwijderen" onClick={() => removeCustom('scrap', i)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="admin__table">
            <thead>
              <tr><th>Naam</th><th>Gewicht</th><th>Aankoopprijs €</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {(baseline[tab] as (PriceBar | PriceCoin)[]).map(row => {
                const pinned = row.name in ov[tab];
                return (
                  <tr key={row.name} className={pinned ? 'admin__row--pinned' : ''}>
                    <td className="admin__name">{row.name}</td>
                    <td className="admin__muted">{row.weight}</td>
                    <td><input className="admin__in admin__in--num" type="number" step="0.01"
                      value={pinned ? ov[tab][row.name] : row.price}
                      onChange={e => pin(tab, row.name, num(e.target.value))} /></td>
                    <td>{pinned
                      ? <span className="admin__badge admin__badge--pinned">Handmatig</span>
                      : <span className="admin__badge">Auto</span>}</td>
                    <td>{pinned && <button className="admin__revert" title="Terug naar automatisch" onClick={() => unpin(tab, row.name)}>↺ auto</button>}</td>
                  </tr>
                );
              })}
              {(ov.custom[tab] as (PriceBar | PriceCoin)[]).map((row, i) => (
                <tr key={`c${i}`} className="admin__row--pinned">
                  <td><input className="admin__in" placeholder="Naam" value={row.name} onChange={e => setCustom(tab, i, 'name', e.target.value)} /></td>
                  <td><input className="admin__in admin__in--w" placeholder="bv. 5 g" value={row.weight} onChange={e => setCustom(tab, i, 'weight', e.target.value)} /></td>
                  <td><input className="admin__in admin__in--num" type="number" step="0.01" value={row.price} onChange={e => setCustom(tab, i, 'price', num(e.target.value))} /></td>
                  <td><span className="admin__badge admin__badge--pinned">Eigen</span></td>
                  <td><button className="admin__del" title="Verwijderen" onClick={() => removeCustom(tab, i)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {baseline && !loading && (
          <button className="admin__add" onClick={() => addCustom(tab)}>+ Eigen product toevoegen</button>
        )}

        <p className="admin__hint">
          <strong>Auto</strong> = volgt automatisch de markt (elke ~2 uur). <strong>Handmatig</strong> = door jou vastgezet.
          Wijzig je een bedrag, dan wordt die rij automatisch <strong>Handmatig</strong>; met <strong>↺ auto</strong> zet je hem terug.
          Vergeet niet op <strong>Opslaan</strong> te klikken.
        </p>
      </div>
    </div>
  );
}
